import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getCurrencySymbol, toFixedTrunc } from '../../../client/Juno/helpers';
import PropTypes from 'prop-types';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import clsx from 'clsx';

export default function AmountWithCurrency({
    data,
    transactionLabel,
    amountValue,
    selected,
    errorMessage,
    onAmountChange,
    onChange = () => {},
    onCurrencyChange,
    isErrorVisible = true,
    isRequired = false,
    dataE2e,
    type,
    errorId,
}) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const dropdownRef = useRef(null);

    useOutsideClick(dropdownRef, () => {
        if (dropdownOpen) setDropdownOpen(false);
    });

    const handleSelect = currency => {
        onCurrencyChange(currency);
        setDropdownOpen(false);
    };

    useEffect(() => {
        if (!selected) {
            onCurrencyChange(data[0]);
        }
    }, [data]);

    const imageBaseURl = useSelector(state => state.config.image_base_url);
    const clientData = useSelector(state => state.juno.clientData);
    const { readOnly } = clientData || {};

    return (
        <div className={clsx('w-full  flex flex-col ', readOnly && '!cursor-not-allowed')}>
            <div
                className={`relative py-[20px] px-[16px] rounded-lg w-full   ${
                    errorMessage?.length && isFocused ? 'border-[1px] border-[#FF675D]' : ''
                } bg-card-bg`}>
                <p className="text-xs font-medium font-Inter text-balanceCard-manage mb-2 text-uppercase">
                    {transactionLabel}
                    {isRequired && <span className="text-red-500 ml-1">*</span>}
                </p>
                <div className="flex items-center justify-between">
                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={e => {
                                if (readOnly) return;
                                e.preventDefault();
                                setDropdownOpen(!dropdownOpen);
                            }}
                            data-e2e={`${transactionLabel}-${dataE2e}`}
                            aria-haspopup="listbox"
                            aria-expanded={dropdownOpen}
                            disabled={readOnly}
                            className={clsx(
                                'flex items-center gap-2 bg-transparent focus:outline-none',
                                readOnly && 'cursor-not-allowed',
                            )}>
                            {selected?.currencyIcon ? (
                                <img
                                    src={`${imageBaseURl}${selected?.currencyIcon}`}
                                    alt="flag"
                                    className="w-5 h-5 rounded-full"
                                />
                            ) : null}
                            <span className="text-lg font-medium !text-text">
                                {selected?.currencyShortName}
                            </span>
                            {imageBaseURl && (
                                <div className="flex items-center justify-center w-6 h-6 rounded-[4px] hover:bg-[#00000014] hover:dark:bg-[#FFFFFF14]">
                                    <img
                                        src={`${imageBaseURl}/junoclient/dropdown-icon.svg`}
                                        alt="dropdown"
                                        className={`invert dark:invert-0 transform transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                                    />
                                </div>
                            )}
                        </button>
                        {dropdownOpen && data?.length ? (
                            <div className="z-3 hide-scrollbar overflow-y-scroll max-h-[200px] fixed w-72 bg-white dark:!bg-[#26272B] rounded-lg shadow-[0px_4px_12px_0px_rgba(0,0,0,0.24)] outline outline-1 outline-offset-[-1px] outline-white dark:outline-zinc-700 inline-flex flex-col justify-start items-start">
                                {data.map(currency => (
                                    <>
                                        <div
                                            key={currency.currencyShortName}
                                            onClick={e => {
                                                handleSelect(currency);
                                                onChange(currency.currencyShortName);
                                            }}
                                            className={` ${
                                                selected?.currencyShortName ===
                                                currency.currencyShortName
                                                    ? 'bg-card-bg'
                                                    : 'bg-white dark:!bg-[#26272B]'
                                            } hover:!bg-card-bg self-stretch pl-4 pr-3 py-2.5 inline-flex justify-start items-center gap-2 cursor-pointer`}>
                                            <img
                                                src={`${imageBaseURl}${currency.currencyIcon}`}
                                                alt="flag"
                                                data-e2e={currency.currencyShortName}
                                                className="w-5 h-5 rounded-full mr-3"
                                            />
                                            <div
                                                className={`flex-1 justify-center !text-text text-sm font-medium uppercase leading-tight`}>
                                                {currency.currencyShortName} (
                                                {currency.currencyShortName == 'USDT'
                                                    ? 'Tether'
                                                    : currency.blockchain ||
                                                      currency.currencyFullName ||
                                                      currency.currencyShortName}
                                                )
                                            </div>
                                            {selected?.currencyShortName ===
                                                currency.currencyShortName && (
                                                <img
                                                    src={`${imageBaseURl}/junoclient/checkmark-icon.svg`}
                                                    alt="dropdown"
                                                    className="invert dark:invert-0"
                                                />
                                            )}
                                        </div>
                                        <div className="self-stretch py-[0.5px] relative bg-card-bg" />
                                    </>
                                ))}
                            </div>
                        ) : null}
                    </div>
                    <input
                        value={amountValue}
                        name={selected?.currencyType == 'crypto' ? 'cryptoAmount' : 'fiatAmount'}
                        aria-label="Currency amount"
                        data-e2e={`${dataE2e}-field`}
                        autoComplete="off"
                        onChange={e => {
                            onAmountChange(e);
                        }}
                        placeholder="0"
                        onFocus={() => {
                            setIsFocused(true);
                        }}
                        onBlur={() => setIsFocused(false)}
                        className={clsx(
                            'bg-transparent text-right font-[Inter] font-medium',
                            '!max-w-[200px] sm:max-w-[150px] min-w-0 w-full',
                            errorMessage?.length && isFocused ? 'text-[#FF675D]' : '!text-text',
                            amountValue?.length > 10 ? 'text-xl' : 'text-2xl',
                        )}
                        disabled={readOnly}
                    />
                </div>
                <div className="text-balanceCard-manage mt-1 font-medium font-Inter text-xs">
                    <span className="text-uppercase">
                        {selected?.currencyShortName?.toLowerCase() === 'usdt' &&
                        type?.toLowerCase() !== 'send'
                            ? 'Tether'
                            : selected?.blockchain || selected?.currencyFullName}
                    </span>
                    <span className="float-right" data-e2e={`${dataE2e}-amount`}>
                        {getCurrencySymbol(selected?.currencyFullName)}{' '}
                        {toFixedTrunc(
                            selected?.balanceAmount,
                            selected?.currencyType == 'crypto' ? 8 : 2,
                        )}
                    </span>
                </div>
            </div>

            {errorMessage?.length > 0 && isErrorVisible && (
                <p
                    data-e2e="error"
                    data-e2e-msg={errorMessage}
                    id={errorId || ''}
                    className="text-[#FF675D] text-sm mt-2 font-medium font-Inter uppercase">
                    {errorMessage}
                </p>
            )}
        </div>
    );
}

AmountWithCurrency.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            currencyFullName: PropTypes.string.isRequired,
            currencyShortName: PropTypes.string.isRequired,
            currencyIcon: PropTypes.string.isRequired,
            balanceAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
    ).isRequired,
    transactionLabel: PropTypes.string.isRequired,
    errorMessage: PropTypes.string,
    amountValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    selected: PropTypes.shape({
        currencyFullName: PropTypes.string,
        currencyShortName: PropTypes.string,
        currencyIcon: PropTypes.string,
        balanceAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    onAmountChange: PropTypes.func.isRequired,
    onCurrencyChange: PropTypes.func.isRequired,
    isRequired: PropTypes.bool,
};
